import { getAnalytics, logEvent } from "firebase/analytics";

/**
 * Generic function to log any analytics event
 */
export function logAnalyticsEvent(eventName: string, eventParams?: any): void {
  const analytics = getAnalytics();
  logEvent(analytics, eventName, eventParams || undefined);
}

/**
 * Announcement Creation Funnel Events
 * These events track the complete user journey through the announcement creation process
 */

// Step 1: User starts creating an announcement
export function trackAnnouncementStarted(params?: { announce_type_created?: string }): void {
  logAnalyticsEvent('announcement_started', {
    timestamp: new Date().toISOString(),
    ...params
  });
}

// Step 2: Basic vehicle information form (listing-form/index.vue)
export function trackVehicleBasicInfoStarted(): void {
  logAnalyticsEvent('vehicle_basic_info_started', {
    step: 'Remplissage automatique',
    timestamp: new Date().toISOString()
  });
}

export function trackVehicleFieldCompleted(fieldName: string, value?: any): void {
  logAnalyticsEvent('vehicle_field_completed', {
    field_name: fieldName,
    step: 'Remplissage automatique',
    has_value: !!value,
    timestamp: new Date().toISOString()
  });
}

export function trackVehicleBasicInfoCompleted(vehicleData: {
  make?: string;
  model?: string;
  year?: number;
  carSerie?: string;
  fuelType?: string;
  trim?: string;
  version?: string;
}): void {
  logAnalyticsEvent('vehicle_basic_info_completed', {
    step: 'Remplissage automatique',
    make: vehicleData.make,
    model: vehicleData.model,
    year: vehicleData.year,
    has_serie: !!vehicleData.carSerie,
    has_fuel_type: !!vehicleData.fuelType,
    has_trim: !!vehicleData.trim,
    has_version: !!vehicleData.version,
    timestamp: new Date().toISOString()
  });
}

// Step 3: Detailed vehicle data form sections
export function trackFormSectionStarted(sectionName: string): void {
  logAnalyticsEvent('form_section_started', {
    section_name: sectionName,
    timestamp: new Date().toISOString()
  });
}

export function trackFormSectionCompleted(sectionName: string, data?: any): void {
  logAnalyticsEvent('form_section_completed', {
    section_name: sectionName,
    has_data: !!data,
    timestamp: new Date().toISOString()
  });
}

export function trackFormSectionError(sectionName: string, errorType: string): void {
  logAnalyticsEvent('form_section_error', {
    section_name: sectionName,
    error_type: errorType,
    timestamp: new Date().toISOString()
  });
}

// Step 4: Image upload tracking
export function trackImageUploadStarted(): void {
  logAnalyticsEvent('image_upload_started', {
    timestamp: new Date().toISOString()
  });
}

export function trackImageUploaded(imageCount: number): void {
  logAnalyticsEvent('image_uploaded', {
    image_count: imageCount,
    images: imageCount,
    timestamp: new Date().toISOString()
  });
}

export function trackImageDeleted(imageCount: number): void {
  logAnalyticsEvent('image_deleted', {
    remaining_count: imageCount,
    timestamp: new Date().toISOString()
  });
}

// Step 5: Announcement submission tracking
export function trackAnnouncementSubmitAttempt(isDraft: boolean): void {
  logAnalyticsEvent('announcement_submit_attempt', {
    announce_status: isDraft ? 'Brouillon' : 'publique',
    timestamp: new Date().toISOString()
  });
}

export function trackAnnouncementCreated(params: {
  announcementId?: string | number;
  isDraft: boolean;
  userId?: string;
  creditsUsed?: number;
  totalAnnouncements?: number; 
}): void {
  logAnalyticsEvent('announcement_created', {
    announcement_id: params.announcementId,
    announce_status: params.isDraft ? 'Brouillon' : 'publique',
    user_id: params.userId,
    credits_used: params.creditsUsed,
    credit_used: params.creditsUsed,
    status: 'succès',
    total_announcements: params.totalAnnouncements || 1,
    timestamp: new Date().toISOString()
  });
}

export function trackAnnouncementUpdated(params: {
  announcementId: string | number;
  isDraft: boolean;
  userId?: string;
}): void {
  logAnalyticsEvent('announcement_updated', {
    announcement_id: params.announcementId,
    announce_status: params.isDraft ? 'Brouillon' : 'publique',
    user_id: params.userId,
    status: 'succès',
    timestamp: new Date().toISOString()
  });
}

export function trackAnnouncementPublished(params: {
  announcementId: string | number;
  userId?: string;
  creditsUsed?: number;
}): void {
  logAnalyticsEvent('announcement_published', {
    announcement_id: params.announcementId,
    user_id: params.userId,
    credits_used: params.creditsUsed,
    credit_used: params.creditsUsed,
    status: 'succès',
    timestamp: new Date().toISOString()
  });
}

export function trackAnnouncementActivated(params: {
  announcementId: string | number;
  userId?: string;
  creditsUsed?: number;
}): void {
  logAnalyticsEvent('announcement_activated', {
    announcement_id: params.announcementId,
    user_id: params.userId,
    credits_used: params.creditsUsed,
    credit_used: params.creditsUsed,
    status: 'succès',
    timestamp: new Date().toISOString()
  });
}

// Failure tracking
export function trackAnnouncementFailed(params: {
  step: string;
  errorType: string;
  errorMessage?: string;
  isDraft?: boolean;
  userId?: string;
}): void {
  logAnalyticsEvent('announcement_failed', {
    step: params.step,
    error_type: params.errorType,
    error_message: params.errorMessage,
    announce_status: params.isDraft ? 'Brouillon' : 'publique',
    user_id: params.userId,
    status: 'échoué',
    timestamp: new Date().toISOString()
  });
}

// Validation error tracking
export function trackValidationError(params: {
  section: string;
  fields: string[];
  errorCount: number;
}): void {
  logAnalyticsEvent('validation_error', {
    section: params.section,
    fields: params.fields.join(','),
    error_count: params.errorCount,
    timestamp: new Date().toISOString()
  });
}

// User abandonment tracking
export function trackAnnouncementAbandoned(params: {
  lastStep: string;
  completionPercentage?: number;
  timeSpent?: number;
}): void {
  logAnalyticsEvent('announcement_abandoned', {
    last_step: params.lastStep,
    completion_percentage: params.completionPercentage,
    time_spent_seconds: params.timeSpent,
    timestamp: new Date().toISOString()
  });
}

// Manual entry tracking
export function trackManualEntrySelected(): void {
  logAnalyticsEvent('manual_entry_selected', {
    reason: 'vehicle_not_in_list',
    timestamp: new Date().toISOString()
  });
}

// Recaptcha tracking
export function trackRecaptchaShown(): void {
  logAnalyticsEvent('recaptcha_shown', {
    timestamp: new Date().toISOString()
  });
}

export function trackRecaptchaVerified(status: boolean): void {
  logAnalyticsEvent('recaptcha_verified', {
    status : status ? 'Recaptcha vérifié' : 'Recaptcha non vérifié',
    timestamp: new Date().toISOString()
  });
}

export function trackPurchasedStatus(status: boolean): void {
  logAnalyticsEvent('purchased_status', {
    status : status ? 'succès' : 'refusé',
    timestamp: new Date().toISOString()
  });
}

export function trackCreditsPurchased(params: {
  userId?: string;
  creditsAdded: number;
  freeCreditsAdded: number;
}): void {
  logAnalyticsEvent('credits_purchased', {
    user_id: params.userId,
    credits_added: params.creditsAdded,
    free_credits_added: params.freeCreditsAdded,
    credit_added: params.creditsAdded,
    free_credit_added: params.freeCreditsAdded,
    timestamp: new Date().toISOString()
  });
}

// Phone click tracking
export function trackPhoneClick(params: {
  page: string;
  announce_id?: string | number;
  conversation_id?: string | number;
}): void {
  logAnalyticsEvent('phone_click', {
    page: params.page,
    announce_id: params.announce_id,
    conversation_id: params.conversation_id,
    timestamp: new Date().toISOString()
  });
}